import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FileText, Plus, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";

interface Note {
  id: string;
  module_week: string;
  content: string;
  timestamp_minutes?: number;
  created_at: string;
  updated_at: string;
}

const DashboardNotes = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [formData, setFormData] = useState({
    module_week: "",
    content: "",
    timestamp_minutes: "",
  });

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("course_notes")
      .select("*")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });

    if (error) {
      toast.error("Failed to load notes");
      console.error(error);
    } else {
      setNotes(data || []);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const noteData = {
      user_id: user.id,
      module_week: formData.module_week,
      content: formData.content,
      timestamp_minutes: formData.timestamp_minutes ? parseInt(formData.timestamp_minutes) : null,
    };

    if (editingNote) {
      const { error } = await supabase
        .from("course_notes")
        .update(noteData)
        .eq("id", editingNote.id);

      if (error) {
        toast.error("Failed to update note");
      } else {
        toast.success("Note updated!");
      }
    } else {
      const { error } = await supabase
        .from("course_notes")
        .insert([noteData]);

      if (error) {
        toast.error("Failed to create note");
      } else {
        toast.success("Note created!");
      }
    }

    setIsDialogOpen(false);
    setEditingNote(null);
    setFormData({ module_week: "", content: "", timestamp_minutes: "" });
    loadNotes();
  };

  const handleEdit = (note: Note) => {
    setEditingNote(note);
    setFormData({
      module_week: note.module_week,
      content: note.content,
      timestamp_minutes: note.timestamp_minutes?.toString() || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("course_notes")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Failed to delete note");
    } else {
      toast.success("Note deleted");
      loadNotes();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold">Course Notes</h1>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingNote(null);
            setFormData({ module_week: "", content: "", timestamp_minutes: "" });
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingNote ? "Edit Note" : "New Note"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="module">Module/Week</Label>
                <Input
                  id="module"
                  value={formData.module_week}
                  onChange={(e) => setFormData({ ...formData, module_week: e.target.value })}
                  placeholder="e.g., Week 1 - Introduction"
                />
              </div>
              <div>
                <Label htmlFor="timestamp">Timestamp (minutes, optional)</Label>
                <Input
                  id="timestamp"
                  type="number"
                  value={formData.timestamp_minutes}
                  onChange={(e) => setFormData({ ...formData, timestamp_minutes: e.target.value })}
                  placeholder="e.g., 15"
                />
              </div>
              <div>
                <Label htmlFor="content">Note Content</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your notes here..."
                  rows={8}
                />
              </div>
              <Button onClick={handleSave} className="w-full">
                {editingNote ? "Update Note" : "Save Note"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {notes.map((note) => (
          <Card key={note.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>{note.module_week}</CardTitle>
                  {note.timestamp_minutes && (
                    <p className="text-sm text-muted-foreground mt-1">
                      @ {note.timestamp_minutes} minutes
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(note)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(note.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{note.content}</p>
              <p className="text-xs text-muted-foreground mt-4">
                Last updated: {new Date(note.updated_at).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        ))}

        {notes.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No notes yet. Start taking notes from your course!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DashboardNotes;
